import React, { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import './AudioPlayer.css';

interface AudioPlayerProps {
  src: string;
  className?: string;
}

export interface AudioPlayerRef {
  reset: () => void;
  getReplayCount: () => number;
}

const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(({ src, className = '' }, ref) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationRef = useRef<number>();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(10); // default to 10s
  const [isReady, setIsReady] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [replayCount, setReplayCount] = useState<number>(0);

  const resetAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
      setIsResetting(true);
      setReplayCount(0);
    }
  }, []);

  const getReplayCount = useCallback(() => {
    return replayCount;
  }, [replayCount]);

  useImperativeHandle(ref, () => ({
    reset: resetAudio,
    getReplayCount: getReplayCount
  }), [resetAudio, getReplayCount]);

  const animate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      animationRef.current = requestAnimationFrame(animate);
    }
  }, []);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, animate]);

  useEffect(() => {
    if (isResetting) {
      const timer = setTimeout(() => setIsResetting(false), 0);
      return () => clearTimeout(timer);
    }
  }, [isResetting]);

  const togglePlay = async () => {
    if (!audioRef.current || !isReady) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Track replay when starting audio after it has ended or been reset
        if (audioRef.current.ended || currentTime === 0) {
          if (audioRef.current.ended) {
            setIsResetting(true);
            setCurrentTime(0);
          }
          // Only increment replay count if this isn't the very first play
          if (replayCount > 0 || currentTime > 0) {
            setReplayCount(prev => prev + 1);
          }
        }
        // for paused state, we want to resume
        if (!audioRef.current.ended) {
          await audioRef.current.play();
        } else {
          audioRef.current.load();
          await audioRef.current.play();
        }
        setIsPlaying(true);
      }
    } catch (err) {
      setError(true);
      setIsPlaying(false);
    }
  };

  const onLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
    setIsReady(true);
    setError(false);
  };

  const onEnded = () => {
    setIsPlaying(false);
    setCurrentTime(duration);
  };

  const onError = () => {
    setError(true);
    setIsReady(false);
    setIsPlaying(false);
  };

  const circumference = 2 * Math.PI * 45; // a bit less than 50 to not touch the border
  const offset = circumference - (currentTime / duration) * circumference;

  return (
    <div className={`audio-player-wrapper ${className}`}>
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={onLoadedMetadata}
        onEnded={onEnded}
        onError={onError}
        preload="metadata"
      />
      <div className="audio-player" onClick={togglePlay}>
        <svg className="progress-ring" width="100" height="100">
          <circle
            className="progress-ring__bg"
            stroke="#ddd"
            strokeWidth="4"
            fill="transparent"
            r="45"
            cx="50"
            cy="50"
          />
          <circle
            className={`progress-ring__circle ${
              isResetting ? 'no-transition' : ''
            }`}
            stroke="#4fa703"
            strokeWidth="4"
            fill="transparent"
            r="45"
            cx="50"
            cy="50"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
            }}
          />
        </svg>
        <button
          className="play-btn"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          disabled={!isReady || error}
        >
          {isPlaying ? (
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              aria-hidden="true"
              focusable="false"
            >
              <rect x="6" y="4" width="4" height="16" fill="currentColor" />
              <rect x="14" y="4" width="4" height="16" fill="currentColor" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              aria-hidden="true"
              focusable="false"
            >
              <polygon points="6,4 20,12 6,20" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>
      {error && (
        <div style={{ color: 'red', fontSize: '0.9rem', marginTop: '0.5rem' }}>
          Audio failed to load.
        </div>
      )}
    </div>
  );
});

export default AudioPlayer; 